/**
 * Rate limit error handling utility
 * Handles HTTP 429 (Too Many Requests) responses gracefully
 */

export interface RateLimitError {
  isRateLimit: boolean;
  message: string;
  retryAfter?: number; // Optional: retry after seconds (if provided by backend)
}

/**
 * Checks if an error response is a rate limit error (HTTP 429)
 * @param error - The error object from axios/API call
 * @param context - Context of the API call (e.g., 'signup', 'otp-send', 'otp-verify')
 * @returns RateLimitError object with user-friendly message
 */
export const handleRateLimitError = (
  error: any,
  context: 'signup' | 'otp-send' | 'otp-verify' | 'google-signup' = 'signup'
): RateLimitError | null => {
  // Check if it's an HTTP 429 error
  const status = error?.response?.status || error?.status;
  
  if (status === 429) {
    // Backend returns retryAfter in response body, also check headers
    const responseData = error?.response?.data || error?.data || {};
    const retryAfter = responseData?.retryAfter 
      || error?.response?.headers?.['retry-after'] 
      || error?.response?.headers?.['Retry-After']
      || error?.retryAfter;

    // Use backend's message if available, otherwise use context-specific message
    let message = responseData?.message || '';
    
    if (!message) {
      // Fallback to context-specific messages
      switch (context) {
        case 'signup':
          message = 'Too many signup attempts. Please try again later.';
          break;
        case 'google-signup':
          message = 'Too many signup attempts. Please try again later.';
          break;
        case 'otp-send':
          message = "You've requested OTP too many times. Please wait before trying again.";
          break;
        case 'otp-verify':
          message = 'Too many verification attempts. Please wait before trying again.';
          break;
        default:
          message = 'Too many requests. Please try again later.';
      }
    }

    // Add retry time if available
    if (retryAfter) {
      const retrySeconds = typeof retryAfter === 'number' ? retryAfter : parseInt(retryAfter, 10);
      if (!isNaN(retrySeconds) && retrySeconds > 0) {
        const minutes = Math.ceil(retrySeconds / 60);
        const hours = Math.floor(minutes / 60);
        
        if (hours > 0) {
          message += ` Please try again in ${hours} hour${hours > 1 ? 's' : ''}.`;
        } else if (minutes > 0) {
          message += ` Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`;
        }
      }
    }

    return {
      isRateLimit: true,
      message,
      retryAfter: retryAfter ? (typeof retryAfter === 'number' ? retryAfter : parseInt(retryAfter, 10)) : undefined,
    };
  }

  return null;
};

/**
 * Formats API error response to include rate limit information
 * @param errorResponse - The error response from API (axios error object)
 * @param context - Context of the API call
 * @returns Formatted error response with rate limit info
 */
export const formatApiErrorResponse = (
  errorResponse: any,
  context: 'signup' | 'otp-send' | 'otp-verify' | 'google-signup' = 'signup'
): any => {
  // Check if it's a rate limit error
  const rateLimitError = handleRateLimitError(errorResponse, context);
  
  if (rateLimitError) {
    // Backend returns: { status: 429, error: "RATE_LIMIT_EXCEEDED", message: "...", retryAfter: 3600 }
    const backendData = errorResponse?.response?.data || {};
    
    return {
      status: 429,
      error: backendData.error || 'RATE_LIMIT_EXCEEDED',
      data: rateLimitError.message,
      message: rateLimitError.message,
      isRateLimit: true,
      retryAfter: rateLimitError.retryAfter,
      // Include backend's original response structure
      ...backendData,
    };
  }

  // Return original error response structure
  const backendData = errorResponse?.response?.data || {};
  return {
    status: errorResponse?.response?.status || 500,
    data: backendData.message || backendData.data || 'An error occurred',
    ...backendData,
  };
};

/**
 * Checks if an error response indicates a rate limit
 * @param errorResponse - The error response to check
 * @returns true if it's a rate limit error
 */
export const isRateLimitError = (errorResponse: any): boolean => {
  return (
    errorResponse?.status === 429 ||
    errorResponse?.isRateLimit === true ||
    errorResponse?.response?.status === 429
  );
};

